import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const values = [
  { title: 'Intention', desc: 'Every decision is purposeful.' },
  { title: 'Storytelling', desc: 'Brands are built through narrative.' },
  { title: 'Luxury Simplicity', desc: 'Elegance lives in restraint.' },
  { title: 'Creative Precision', desc: 'Details define perception.' },
  { title: 'Thoughtful Visibility', desc: 'Presence over noise.' },
];

export default function AboutPage() {
  return (
    <div className="pt-44" data-testid="about-page">
      {/* Hero */}
      <section className="relative" data-testid="about-hero">
        <div className="h-[50vh] md:h-[60vh] relative overflow-hidden">
          <img src="/images/about-desk.jpg" alt="MUSE" className="w-full h-full object-cover object-[center_70%]" data-testid="about-hero-image" />
          <div className="absolute inset-0 bg-background/50" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-main">
              <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
                <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight">
                  The Vision Behind Muse.
                </motion.h1>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Muse Was Created */}
      <section className="py-16 md:py-24" data-testid="about-origin">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-2xl mx-auto">
            <motion.p variants={fadeInUp} className="font-serif text-2xl md:text-3xl font-medium leading-snug mb-10">
              Muse was created from the belief that social media should feel intentional — not random.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
              We saw a gap between strategy and aesthetics. Brands either looked beautiful without direction or strategic without identity.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Muse bridges both.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border max-w-2xl mx-auto" />

      {/* Founder Story */}
      <section className="py-16 md:py-24" data-testid="founder-story">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4 text-center">Meet The Founders</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl font-medium leading-tight mb-8 max-w-3xl mx-auto text-center">
              Built by strategy. Elevated by vision.
            </motion.h2>
            <motion.div variants={fadeInUp} className="max-w-2xl mx-auto text-center mb-12">
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
                Muse Social Creative was founded by Imara Grant and Dean Chambers — bringing together strategy, visual storytelling, social media expertise, and creative production.
              </p>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                The agency was built from the belief that brands deserve more than content. They deserve clarity. Direction. And a presence that feels intentional.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-4xl mx-auto">
              <motion.div variants={fadeInUp} data-testid="founder-imara">
                <div className="mb-8 overflow-hidden">
                  <img src="/images/founder-imara.jpg" alt="Imara Grant" className="w-full h-[400px] object-cover object-top" />
                </div>
                <p className="text-sm tracking-widest uppercase text-butter font-semibold mb-2">Founder</p>
                <h3 className="font-serif text-2xl font-medium mb-1">Imara Grant</h3>
                <p className="text-sm text-muted-foreground mb-6">Strategy & Brand Direction</p>
                <p className="text-muted-foreground leading-relaxed text-sm mb-3">
                  With over a decade of experience in social media, influencer partnerships, marketing, and brand development, Imara brings a strategic lens to every project.
                </p>
                <p className="text-muted-foreground leading-relaxed text-sm mb-3">
                  Her work spans beauty, hospitality, lifestyle, and founder-led brands — helping clients refine how they communicate and position themselves.
                </p>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  She approaches social media as more than content. For her, it is a reflection of identity.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} data-testid="founder-dean">
                <div className="mb-8 overflow-hidden">
                  <img src="/images/founder-dean.jpg" alt="Dean Chambers" className="w-full h-[400px] object-cover object-top" />
                </div>
                <p className="text-sm tracking-widest uppercase text-butter font-semibold mb-2">Co-Founder</p>
                <h3 className="font-serif text-2xl font-medium mb-1">Dean Chambers</h3>
                <p className="text-sm text-muted-foreground mb-6">Creative Direction & Production</p>
                <p className="text-muted-foreground leading-relaxed text-sm mb-3">
                  Dean brings visual storytelling to life through photography, creative direction, and campaign production.
                </p>
                <p className="text-muted-foreground leading-relaxed text-sm mb-3">
                  His work is rooted in editorial aesthetics, elevated composition, and cinematic storytelling.
                </p>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  Together, strategy and visual execution create a seamless experience for Muse clients.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* Values */}
      <section className="py-16 md:py-24" data-testid="values-section">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4 text-center">What Guides Our Work</motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mt-16 max-w-5xl mx-auto">
              {values.map((v, i) => (
                <motion.div key={i} variants={fadeInUp} className="text-center" data-testid={`value-${i}`}>
                  <h3 className="font-serif text-lg font-medium mb-2">{v.title}</h3>
                  <p className="text-muted-foreground text-sm">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* Trusted By */}
      <section className="py-16 md:py-24" data-testid="about-trusted">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Trusted By Brands & Founders</motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Our collaborations range from emerging brands to established names seeking elevated digital presence.
            </motion.p>
            <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
              <div className="flex flex-wrap items-center justify-center gap-8">
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-aroma360.jpg" alt="Aroma360 Luxury Scenting" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-hotel-collection.jpg" alt="Hotel Collection" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-miami-beach.jpg" alt="Miami Beach" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-itsa10.jpg" alt="It's a 10 Haircare" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-we-the-best.jpg" alt="We The Best" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-korbel.jpg" alt="Korbel California Champagne" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-empowerher.png" alt="EmpowerHER" className="max-h-full max-w-full object-contain" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-yuzu.png" alt="Yuzu" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-32 w-64 flex items-center justify-center"><img src="/images/logo-sea-salt.jpg" alt="Sea and Salt Spa" className="max-h-full max-w-full object-contain mix-blend-multiply" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-sc.png" alt="SC" className="max-h-full max-w-full object-contain" /></div>
                <div className="h-16 w-32 flex items-center justify-center"><img src="/images/logo-ca.png" alt="CA" className="max-h-full max-w-full object-contain" /></div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-foreground" data-testid="about-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-background mb-10">
              Let's Build Something Meaningful.
            </motion.h2>
            <motion.div variants={fadeInUp}>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="about-cta-btn">
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
